import type {AccountType} from "@hosttools/core/constant";
import {originalChannels} from "@hosttools/core/constant";
import {
    AddIcon,
    ButtonIconTooltip,
    HStack,
    ListingChannelsLogo,
    useDisclose,
    UserContext,
    VStack
} from "@hosttools/frontend";
import type {Listing} from "@hosttools/frontend/models/listing";
import type {CellContext} from "@tanstack/react-table";
import type React from "react";
import {memo, useCallback, useContext} from "react";

import ModalConnectChannels from "../../ModalConnectChannels";

import useConfirmCloseChannelConnector from "@/client/hooks/useConfirmCloseChannelConnector";
import useConfirmRemoveChannel from "@/client/hooks/useConfirmRemoveChannel";

type Props = StrictUnion<
    | {
          listing: Listing;
      }
    | CellContext<Listing, unknown>
> & {
    className?: string;
    canRemoveChannel?: boolean;
};

const ListingChannels: React.FC<Props> = ({
    listing: listingParams,
    row,
    canRemoveChannel = true,
    className
}) => {
    const {
        user: {canEditAccount}
    } = useContext(UserContext);
    const listing = listingParams || row.original;
    const {isOpen, onClose, onOpen} = useDisclose(false);
    const handleRemoveChannel = useConfirmRemoveChannel(listing);
    const handleCloseChannelConnector = useConfirmCloseChannelConnector(listing);

    const handleRemove = useCallback(
        (channel: AccountType | (string & {})) => {
            if (originalChannels.includes(channel)) {
                handleRemoveChannel(channel);
                return;
            }

            handleCloseChannelConnector(channel);
        },
        [handleCloseChannelConnector, handleRemoveChannel]
    );

    return (
        <VStack className={className}>
            <HStack className="items-center" space="sm">
                <ListingChannelsLogo
                    showDirectBooking
                    showStatus
                    listing={listing}
                    size="lg"
                    onRemove={canEditAccount && canRemoveChannel ? handleRemove : undefined}
                />
                {canEditAccount && (
                    <ButtonIconTooltip
                        action="primary"
                        className="rounded-full border border-dashed border-primary-200"
                        icon={AddIcon}
                        size="sm"
                        tooltip="Connect channels"
                        onPress={onOpen}
                    />
                )}
            </HStack>
            {isOpen && <ModalConnectChannels listing={listing} onClose={onClose} />}
        </VStack>
    );
};

export default memo(ListingChannels);
